import {ReactNode, useContext, useEffect, useState} from "react";
import Page from "../../../api/Page";
import QRCodeIcon from "../../../api/qr-code/icon/QRCodeIcon";
import AuthContext from "../../auth/AuthContext";
import QRCodeIconApi from "../../../api/qr-code/icon/QRCodeIconApi";
import GetListQuery from "../../../api/qr-code/icon/GetListQuery";
import QRCodeIconContext from "./QRCodeIconContext";
import CreateAndPutPayload from "../../../api/qr-code/icon/CreateAndPutPayload";

type WrapQrCodeIconContextProps = {
    /**
     * Children to be wrapped in context
     * @author Kacper Faber
     * @since 0.1.9
    */
    children: ReactNode;
};

/**
 * Default query for fetching the data
 * @author Kacper Faber
 * @since 0.1.9
*/
const defaultQuery: GetListQuery = { limit: 100 };

function WrapQrCodeIconContext(props: WrapQrCodeIconContextProps) {
    /**
     * Data
     * @author Kacper Faber
     * @since 0.1.9
    */
    const [data, setData] = useState<Page<QRCodeIcon>>();

    /**
     * Fetch query.
     * If you change it, system will reload the data
     * @author Kacper Faber
     * @since 0.1.9
    */
    const [query, setQuery] = useState<GetListQuery>(defaultQuery);

    /**
     * Authentication context
     * @author Kacper Faber
     * @since 0.1.9
    */
    const authContext = useContext(AuthContext);

    /**
     * Reload the data when user or query is changed
     * @author Kacper Faber
     * @since 0.1.9
    */
    const reload = () => {
        if (!authContext.user) {
            setData(undefined);
            return;
        }

        QRCodeIconApi.getList(query)
            .then(setData);
    }

    /**
     * Create the new QRCodeIcon, refresh the state and returns the promise.
     * Does not show notifications etc.
     * @author Kacper Faber
     * @since 0.1.9
    */
    const create = async (payload: CreateAndPutPayload) => {
        const icon = await QRCodeIconApi.create(payload);
        reload();
        return icon;
    }

    useEffect(reload, [authContext.user, query]);

    return (
        <QRCodeIconContext.Provider
            children={props.children}
            value={{ data, setData, query, setQuery, reload, create }}
        />
    );
}

export default WrapQrCodeIconContext;